<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import SheetModal from '@/components/ui/SheetModal.vue'
import alertIcon from '@/assets/icons/sorenson/alert-circle.svg'
import successIcon from '@/assets/icons/sorenson/check-circle.svg'

// Alert / confirm dialog in the LeaveSessionModal style. Usually opened through the
// dialog service ($dialog.alert / $dialog.confirm) rather than placed in a template.
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String, // 'warning' | 'error' | 'success'
    default: 'warning'
  },
  title: {
    type: String,
    default: ''
  },
  text: {
    type: String,
    default: ''
  },
  confirmLabel: {
    type: String,
    default: 'OK'
  },
  // no cancel button when empty
  cancelLabel: {
    type: String,
    default: ''
  },
  // red confirm button for destructive actions
  danger: {
    type: Boolean,
    default: false
  },
  // only the buttons can close it: no Escape, outside click or swipe
  blocking: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const iconSrc = computed(() => (props.icon === 'success' ? successIcon : alertIcon))

const confirm = () => {
  emit('confirm')
  emit('update:modelValue', false)
}

const cancel = () => {
  emit('cancel')
  emit('update:modelValue', false)
}
</script>

<template>
  <sheet-modal
      :model-value="modelValue"
      @update:model-value="emit('update:modelValue', $event)"
      exact-width="w-[350px]"
      body-class="px-5 pt-[30px] pb-6"
      :blocking="blocking"
      :has-close="false"
      :scrollable="false"
  >
    <div class="flex flex-col items-center text-center" :data-testid="`alert-modal-${icon}`">
      <img :src="iconSrc" class="size-[52px]" alt="">
      <h2 v-if="title" class="mt-[30px] text-white text-2xl font-bold leading-[30px]" data-testid="alert-modal-title">{{ title }}</h2>
      <p
          v-if="text"
          :class="title ? 'mt-2 text-text-neutral-high text-base leading-6' : 'mt-[30px] text-white text-lg font-medium leading-7'"
          data-testid="alert-modal-text"
      >{{ text }}</p>

      <div class="mt-7 w-full flex flex-col gap-2">
        <button
            type="button"
            class="w-full h-12 px-5 rounded-lg text-black text-base font-medium leading-normal transition duration-160 cursor-pointer"
            :class="danger ? 'bg-[#FF6566] hover:bg-[#FF6566]/90' : 'bg-[#6CBFFF] hover:bg-[#6CBFFF]/90'"
            @click="confirm"
            data-testid="alert-modal-confirm"
        >
          {{ confirmLabel }}
        </button>
        <button
            v-if="cancelLabel"
            type="button"
            class="w-full h-12 px-5 rounded-lg text-[#6CBFFF] hover:text-[#6CBFFF]/80 text-base font-medium leading-normal transition duration-160 cursor-pointer"
            @click="cancel"
            data-testid="alert-modal-cancel"
        >
          {{ cancelLabel }}
        </button>
      </div>
    </div>
  </sheet-modal>
</template>
